import React, { useEffect, useRef } from 'react';
import { ScrollArea, UnstyledButton } from '@mantine/core';
import './TimeColumns.scss';

const pad2 = (n: number): string => String(n).padStart(2, '0');
const range = (n: number): string[] => Array.from({ length: n }, (_, i) => pad2(i));

const HOURS = range(24);
const MINUTES = range(60);
const SECONDS = range(60);

interface ITimeColumnProps {
    label: string;
    values: string[];
    selected: string;
    onPick: (value: string) => void;
}

const TimeColumn: React.FC<ITimeColumnProps> = ({ label, values, selected, onPick }) => {
    const selectedRef = useRef<HTMLButtonElement | null>(null);

    // Центрируем выбранное значение в своём вьюпорте при открытии — без scrollIntoView,
    // чтобы не утянуть саму страницу/модалку (dropdown в портале).
    useEffect(() => {
        const btn = selectedRef.current;
        const viewport = btn?.closest<HTMLElement>('.rf-time-columns__viewport');
        if (!btn || !viewport) return;
        const b = btn.getBoundingClientRect();
        const v = viewport.getBoundingClientRect();
        viewport.scrollTop += b.top - v.top - (v.height - b.height) / 2;
    }, []);

    return (
        <div className="rf-time-columns__col">
            <div className="rf-time-columns__header">{label}</div>
            <ScrollArea.Autosize
                mah={200}
                type="auto"
                offsetScrollbars="y"
                scrollbarSize={8}
                classNames={{ viewport: 'rf-time-columns__viewport' }}
            >
                <div className="rf-time-columns__list">
                    {values.map((v) => {
                        const isSelected = v === selected;
                        return (
                            <UnstyledButton
                                key={v}
                                ref={isSelected ? selectedRef : undefined}
                                className="rf-time-columns__option"
                                data-selected={isSelected || undefined}
                                onClick={() => onPick(v)}
                            >
                                {v}
                            </UnstyledButton>
                        );
                    })}
                </div>
            </ScrollArea.Autosize>
        </div>
    );
};

export interface ITimeColumnsProps {
    /** Текущее время HH:mm[:ss] либо '' */
    value: string;
    withSeconds: boolean;
    onChange: (next: string) => void;
}

/**
 * Колоночный выбор времени (часы / минуты / [секунды]) — выпадашка в одном
 * визуальном языке с календарём (та же подсветка selected/hover, скругления).
 * Каждый столбец независимо скроллится; недостающие части добиваются '00'.
 */
const TimeColumns: React.FC<ITimeColumnsProps> = ({ value, withSeconds, onChange }) => {
    const [hh = '', mm = '', ss = ''] = value ? value.split(':') : [];
    const commit = (h: string, m: string, s: string) => onChange(withSeconds ? `${h}:${m}:${s}` : `${h}:${m}`);

    return (
        <div className="rf-time-columns">
            <TimeColumn label="Час" values={HOURS} selected={hh} onPick={(v) => commit(v, mm || '00', ss || '00')} />
            <TimeColumn label="Мин" values={MINUTES} selected={mm} onPick={(v) => commit(hh || '00', v, ss || '00')} />
            {withSeconds && (
                <TimeColumn
                    label="Сек"
                    values={SECONDS}
                    selected={ss}
                    onPick={(v) => commit(hh || '00', mm || '00', v)}
                />
            )}
        </div>
    );
};

export default TimeColumns;
